import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";

import { authApi } from "../services/api";
import { setRefreshTokenFunction } from "../services/api";
import { tokenManager } from "../helpers/token_manager";

interface User {
  id: number;
  login: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  login: (token: string, user: User, expiresAt: string, credentials?: { login: string; password: string }) => void;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const isRefreshingRef = useRef(false);
  const refreshPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    const savedToken = localStorage.getItem("auth_token");
    const savedUser = localStorage.getItem("auth_user");
    const savedExpiresAt = localStorage.getItem("tokenExpiresAt");

    if (savedToken && savedUser && savedExpiresAt) {
      try {
        const parsedUser = JSON.parse(savedUser);
        if (new Date(savedExpiresAt).getTime() > Date.now()) {
          setToken(savedToken);
          setUser(parsedUser);
          setIsAuthenticated(true);
        } else {
          console.log("AUTH: Token expired");
          logout();
        }
      } catch (error) {
        console.error("AUTH: Failed to parse saved auth data:", error);
        logout();
      }
    }
  }, []);

  const login = useCallback((
    newToken: string,
    newUser: User,
    expiresAt: string,
    credentials?: { login: string; password: string }
  ) => {
    setToken(newToken);
    setUser(newUser);
    setIsAuthenticated(true);
    localStorage.setItem("auth_token", newToken);
    localStorage.setItem("auth_user", JSON.stringify(newUser));
    localStorage.setItem("tokenExpiresAt", expiresAt);

    if (credentials) {
      localStorage.setItem("auth_login", credentials.login);
      localStorage.setItem("auth_password", credentials.password);
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    localStorage.removeItem("tokenExpiresAt");
    localStorage.removeItem("auth_login");
    localStorage.removeItem("auth_password");

    tokenManager.reset();
  }, []);

  const refreshToken = useCallback(async (): Promise<void> => {
    if (isRefreshingRef.current && refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const savedLogin = localStorage.getItem("auth_login");
    const savedPassword = localStorage.getItem("auth_password");

    if (!savedLogin || !savedPassword) {
      console.warn("No credentials saved for token refresh");
      logout();
      window.location.href = "/login";
      return;
    }

    isRefreshingRef.current = true;

    refreshPromiseRef.current = (async () => {
      try {
        console.log("Refreshing token...");
        const response = await authApi.login({
          login: savedLogin,
          password: savedPassword,
        });

        const newUser = { id: 1, login: savedLogin };
        login(response.token, newUser, response.expiresAt);
        console.log("Token refreshed successfully");
      } catch (error: any) {
        console.error("Failed to refresh token:", error);

        const status = error.response?.status;
        if (status === 401 || status === 403) {
          logout();
          window.location.href = "/login";
        } else {
          console.warn("Token refresh failed but keeping session due to non-auth error");
        }
      } finally {
        isRefreshingRef.current = false;
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  }, [login, logout]);

  useEffect(() => {
    setRefreshTokenFunction(refreshToken);
  }, [refreshToken]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        token,
        login,
        logout,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};